import { Injectable } from '@angular/core';
import { ServiceBase } from './base/service.base';
import { Observable } from 'rxjs';
import { OrigamiHttpResponse, OrigamiResponse } from '@app/origami-api/services/base/service.base';
import { CollaborationReviewType } from '@app/origami-api/constants/collaborative-review-type';
import { CollaborationReviewDocumentType } from '@app/origami-api/constants/collaboration-review-document-type';
import { CollaborationReviewParticipantRole } from '@app/origami-api/constants/collaboration-review-participant-role';
import { CollaborationDocumentReviewStatus } from '@app/origami-api/constants/collaboration-document-review-status';
import { DocumentReviewStatus } from '@app/origami-api/constants/document-review-status';
import { CollaborationReviewStatus, ErrorCode, RoleLevel } from '@app/origami-api/constants';
import { TopicField } from '@app/origami-api/utils/topic-utils';

export interface CollaborationReviewDocument {
  DocumentName: string;
  ReviewDocumentId?: number;
  CreatedBy?: number;
  CreatedDate?: string;
  DocumentTopicParentId?: number;
  DocumentTopicId?: number;
  FilePath?: string;
  FileToken?: string;
  LastUpdatedBy?: string;
  LastUpdatedDate?: string;
  OriginalFileName?: string;
  ReviewDocumentTopicId?: number;
  ReviewDocumentType?: CollaborationReviewDocumentType;
  ReviewId?: number;
  TemplateTopicId?: number;
}

export interface ReviewParticipant {
  ParticipantId: number;
  ParticipantRole: CollaborationReviewParticipantRole;
  ParticipantRoleLevel?: RoleLevel;
  ParticipantName?: string;
  ParticipantFullName?: string;
  ReviewId?: number;
}

export interface CreateCollaborationReviewOptions {
  siteId: number;
  collaborationReview: {
    Title: string;
    Description?: string;
    DueDate: string;
    ReviewType: CollaborationReviewType;
    ReviewDocuments: {
      DocumentName: string;
      FileToken?: string;
      ReviewDocumentType?: CollaborationReviewDocumentType;
    }[];
    ReviewParticipants: ReviewParticipant[];
  };
}

export interface CollaborationReview {
  Title: string;
  Description?: string;
  DueDate: string;
  ReviewType: CollaborationReviewType;
  ReviewDocuments: CollaborationReviewDocument[];
  ReviewParticipants: ReviewParticipant[];
  CreatedBy: number;
  CreatedDate: string;
  LastUpdatedBy: number;
  LastUpdatedByName?: string;
  LastUpdatedDate: string;
  AdditionalFields?: TopicField[]; // todo? rejection status?
  ReviewId: number;
  ReviewStatus: CollaborationReviewStatus;
  ReviewUserStatus?: CollaborationReviewStatus;
  FileVersionId?: string;
  FileVersion?: string;
  ReviewUserComments?: string;
  DocumentReviewStatus?: DocumentReviewStatus;
}

export interface ExtendedCollaborationReview extends CollaborationReview {
  VersionCreatedDateFormatted?: Date;
  CollaborationStartDate?: Date;
  CollaborationOwner?: string;
  CreatedByUserName?: string;
  ReviewClosedBy?: string;
  ReviewParticipantsUnique?: ReviewParticipant[];
  StartVersion?: string;
  DocumentTitle?: string;
  OtherDocumentOwnersExist?: boolean;
}

export interface GetCollaborationReviewOptions {
  reviewId: number;
  siteId: number;
}

export interface ModifyCollaborationReviewOptions {
  siteId: number;
  collaborationReview: CollaborationReview;
}

export interface ReviewView {
  EntitiesCount: number;
  ViewFilterDisplayName: string;
  ViewFilterId: string;
  ViewMode: number;
}

export interface GetCollaborationReviews {
  limit?: number;
  siteId: number;
  start?: number;
  viewFilter: string;
  viewMode: number;
  reviewId?: number;
  orderBy?: string;
}

export interface AddCollaborationReviewLogOptions {
  siteId: number;
  reviewId: number;
  reviewDocumentId: number;
  reviewStatusId: CollaborationDocumentReviewStatus;
  comments: string;
}

export interface ReviewLogItem {
  Comments: string;
  CreatedDate: string;
  LogId: number;
  ParticipantId: number;
  FullName: string;
  ReviewDocumentId: number;
  StatusId: CollaborationDocumentReviewStatus;
}

export interface CompleteCollaborationReviewOptions {
  siteId: number;
  reviewId: number;
  topicId?: number;
  documentReviewStatus: DocumentReviewStatus;
  comments: string;
  disconnectUsers: boolean;
  scheduleNextReviewDate?: boolean;
}

@Injectable()
export class CollaborationReviewService extends ServiceBase {
  serviceUrl = 'CollaborationReviewService.svc';

  createCollaborationReview(body: CreateCollaborationReviewOptions) {
    return this.origamiPost('/CreateCollaborationReview', body, null, null, true);
  }

  startReview(body): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/StartReview', body, null, null, true);
  }

  getCollaborationReview(
    body: GetCollaborationReviewOptions,
    ...params
  ): Observable<OrigamiResponse<CollaborationReview>> {
    return this.origamiPost('/GetCollaborationReview', body, ...params);
  }

  modifyCollaborationReview(body: ModifyCollaborationReviewOptions | any) {
    // TODO: delete 'any' after refactoring in documents-cart-review.component
    return this.origamiPost('/ModifyCollaborationReview', body, null, null, true);
  }

  getCollaborationReviewViews(
    body: { siteId: number; viewMode: number },
    ...params
  ): Observable<OrigamiResponse<ReviewView[]>> {
    return this.origamiPost('/GetCollaborationReviewViews', body, ...params);
  }

  getCollaborationReviews(
    body: GetCollaborationReviews,
    ...params
  ): Observable<OrigamiResponse<CollaborationReview[] | any>> {
    return this.origamiPost('/GetCollaborationReviews', body, ...params);
  }

  addCollaborationReviewDocumentLog(body: AddCollaborationReviewLogOptions): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/AddCollaborationReviewDocumentLog', body, null, null, true);
  }

  getCollaborationReviewDocumentLog(
    body: { siteId: number; documentReviewId: number },
    ...params
  ): Observable<OrigamiResponse<ReviewLogItem[]>> {
    return this.origamiPost('/GetCollaborationReviewDocumentLog', body, ...params);
  }

  completeCollaborationReview(
    body: CompleteCollaborationReviewOptions,
    options?: any,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/CompleteCollaborationReview', body, options, silentErrorCodes, true);
  }
}
